import React, { useState } from "react";
import { Button, Modal, Select, message } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { IOrder, IOrderShort, IRatedOrder, OrderDeliveryMode } from "../../../models/order/response";
import { getDeliveryModeName } from "../../../models/order/enum";
import { isOrderInActiveTrip } from "../../../utils/helpers/trip-helpers";
import EndPoints from "../../../services/end-points";
import { Permissions } from "../../../utils/Permissions/Permissions";

interface IDeliveryModeEditButtonProps {
  order: IOrder | IOrderShort | IRatedOrder;
  onSuccess?: () => void;
  size?: "small" | "middle" | "large";
}

const DeliveryModeEditButton: React.FC<IDeliveryModeEditButtonProps> = ({
  order,
  onSuccess,
  size = "small",
}) => {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedMode, setSelectedMode] = useState<OrderDeliveryMode>(
    order.deliveryMode ?? OrderDeliveryMode.LEGACY
  );

  const userPermissions = localStorage.getItem("permisionStoroge")?.split(",") || [];

  // Check if user has permission
  const hasPermission = userPermissions.includes(Permissions.CAN_UPDATE_ORDER);

  const handleOpenModal = () => {
    // Prevent opening modal if order is in active trip
    if (isOrderInActiveTrip(order)) {
      message.error(t("cannot_change_delivery_mode_order_in_trip"));
      return;
    }

    setSelectedMode(order.deliveryMode ?? OrderDeliveryMode.LEGACY);
    setModalVisible(true);
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);

      await EndPoints.order.updateDeliveryMode(order.id, {
        deliveryMode: selectedMode,
      });

      message.success(t("delivery_mode_updated_successfully"));
      setModalVisible(false);

      // Call onSuccess callback to refresh data
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Failed to update delivery mode:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    setSelectedMode(order.deliveryMode ?? OrderDeliveryMode.LEGACY);
  };

  // Don't render button if user doesn't have permission
  if (!hasPermission) {
    return null;
  }

  // Disable button if order is in active trip
  const isDisabled = isOrderInActiveTrip(order);

  return (
    <>
      <Button
        type="primary"
        shape="circle"
        size={size}
        icon={<EditOutlined />}
        onClick={handleOpenModal}
        disabled={isDisabled}
        title={t("change_delivery_mode")}
      />

      <Modal
        title={t("change_delivery_mode")}
        open={modalVisible}
        onOk={handleConfirm}
        onCancel={handleCancel}
        confirmLoading={loading}
        okText={t("confirm")}
        cancelText={t("cancel")}
        width={400}
      >
        <div style={{ padding: "20px 0" }}>
          <p style={{ marginBottom: "16px" }}>
            {t("select_delivery_mode")}:
          </p>
          <Select
            value={selectedMode}
            onChange={setSelectedMode}
            style={{ width: "100%" }}
            size="large"
          >
            <Select.Option value={OrderDeliveryMode.LEGACY}>
              {t(getDeliveryModeName(OrderDeliveryMode.LEGACY))}
            </Select.Option>
            <Select.Option value={OrderDeliveryMode.TRIP_BASED}>
              {t(getDeliveryModeName(OrderDeliveryMode.TRIP_BASED))}
            </Select.Option>
          </Select>
        </div>
      </Modal>
    </>
  );
};

export default DeliveryModeEditButton;
